import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from 'lib/utils';
import { Loader2Icon } from 'lucide-react';
import { Slot } from 'radix-ui';
import * as React from 'react';

/**
 * Figma: "Button - Nova" (Button & Icon Button page).
 *
 * Variants Primary / Secondary / Outline / Ghost / Link / Destructive, sizes
 * Default / Large (+ Small and icon sizes from shadcn). Buttons are capsules
 * (`rounded-full`, Figma `radius-full`) and the default size is 32px tall with
 * a 14px Medium label, 10px inline padding and a 6px gap, as in the library.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-ring-destructive/20 aria-invalid:border-ring-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-ring-destructive/20',
        outline: 'border border-border bg-card text-foreground shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
        link: 'text-link underline-offset-4 hover:underline hover:text-link-hover',
        /** A link whose action has a consequence worth a second look (no Figma counterpart yet). */
        'link-warning': 'text-warning underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-8 px-2.5 py-1.5 has-[>svg]:px-2',
        sm: 'h-7 gap-1 px-2 text-xs has-[>svg]:px-1.5',
        lg: 'h-10 px-4 text-base has-[>svg]:px-3',
        /** Full-width mobile call to action. */
        xl: 'h-12 px-6 text-base has-[>svg]:px-4',
        icon: 'size-8',
        'icon-sm': 'size-7',
        'icon-lg': 'size-10',
      },
    },
    compoundVariants: [
      // A link is inline text: no capsule box, so it aligns with the copy around it
      { variant: 'link', class: 'h-auto px-0 py-0' },
      { variant: 'link-warning', class: 'h-auto px-0 py-0' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    /** Shows a spinner in place of the leading icon and disables the button. */
    loading?: boolean;
    /**
     * Marks a toggle button as chosen. Renders `aria-pressed` and, for the
     * functional test suite, `data-selected`.
     */
    selected?: boolean;
  };

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading,
  selected,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-selected={selected === undefined ? undefined : selected}
      aria-pressed={selected}
      className={cn(buttonVariants({ variant, size, className }), selected && 'ring-2 ring-inset ring-primary')}
      disabled={disabled || loading}
      {...props}
    >
      {asChild ? (
        // Slot needs exactly one child; the spinner is only for real buttons
        children
      ) : (
        <>
          {loading && <Loader2Icon className="animate-spin" />}
          {children}
        </>
      )}
    </Comp>
  );
}

export { Button, buttonVariants };
